import {
  GROUPS_API_URL,
  GROUPS_FILTER_URL,
  GROUPS_NAME_CHECK_URL,
  GROUPS_REDUCE_ALL_URL,
  PROJECTION,
  QUESTION_MODELS_API_URL,
  VEHICLE_CHECK_MODELS_API_URL,
} from 'constants/ApiConstants';
import { MAX_RECORD } from 'constants/Global';

import * as actionTypes from '../actionTypes';

// ===========================================================================
// CRUD Group Actions
// ===========================================================================

/**
 * Creates a new group with the given name.
 * @param {string} name - The name for the new group.
 * @returns {import('../types/actions').CreateGroupAction}
 */
export const createGroupRequest = name => ({
  type: actionTypes.CREATE_GROUP_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${GROUPS_API_URL}`,
      data: { name },
    },
    name,
  },
});

/**
 * Updates an existing group.
 * @param {import('../types/group').Group} group - The group data to update.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').UpdateGroupAction}
 */
export const updateGroupRequest = (group, meta = {}) => ({
  type: actionTypes.UPDATE_GROUP_REQUEST,
  payload: {
    request: {
      method: 'PUT',
      url: `${GROUPS_API_URL}/${group.id}`,
      data: group,
    },
  },
  meta,
});

/**
 * Fetches a group by ID with optional query parameters.
 * @param {string} groupId - The unique identifier of the group.
 * @param {Object} params - Query parameters for the request.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').FetchGroupAction}
 */
export const fetchGroup = (groupId, params, meta = {}) => ({
  type: actionTypes.FETCH_GROUP_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${GROUPS_API_URL}/${groupId}`,
      params,
    },
    groupId,
  },
  meta,
});

/**
 * Success action for fetching a group.
 * @param {import('../types/group').Group} group - The fetched group data.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').FetchGroupSuccessAction}
 */
export const fetchGroupSuccess = (group, meta = {}) => ({
  type: actionTypes.FETCH_GROUP_SUCCESS,
  payload: group,
  meta,
});

/**
 * Failure action for fetching a group.
 * @param {string} error - The error message.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').FetchGroupFailureAction}
 */
export const fetchGroupFailure = (error, meta = {}) => ({
  type: actionTypes.FETCH_GROUP_FAILURE,
  payload: error,
  error: true,
  meta,
});

// ===========================================================================
// Various Group Requests Actions
// ===========================================================================

/**
 * Fetches multiple groups with filter parameters.
 * @param {Object} params - Query parameters for filtering groups.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').FetchGroupsSelectAction}
 */
export const fetchGroupsSelect = (params, meta = {}) => ({
  type: actionTypes.FETCH_GROUPS_SELECT_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: GROUPS_FILTER_URL,
      params: { ...params, size: MAX_RECORD },
    },
  },
  meta,
});

/**
 * Fetches groups filtered by a specific extension.
 * @param {import("modules/global/typing/enums/extensions.enum").ExtensionEnum} extension - The extension to filter groups by.
 * @returns {import('../types/actions').FetchGroupsByExtensionAction}
 */
export const fetchGroupsByExtension = extension => ({
  type: actionTypes.FETCH_GROUPS_BY_EXTENSION_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: GROUPS_REDUCE_ALL_URL,
      params: {
        status: 'ACTIVE',
        extensions: extension,
        reducer: ['id,none', 'name,none'],
      },
    },
  },
  meta: {
    extension,
  },
});

/**
 * @param {string} name The name to look for.
 * @param {Object} [meta={}]
 * @returns {import('../types/actions').HeadGroupAction}
 */
export const findOneByName = (name, meta = {}) => ({
  type: actionTypes.FIND_ONE_BY_NAME,
  payload: {
    request: {
      method: 'HEAD',
      url: GROUPS_NAME_CHECK_URL,
      params: {
        name,
      },
    },
  },
  meta,
});

/**
 * @param {string} name The name looked for.
 * @param {boolean} found Whether the name exists or not in the database.
 * @return {import('../types/actions').HeadGroupSuccessAction}
 */
export const findOneByNameSuccess = (name, found, meta = {}) => ({
  type: actionTypes.FIND_ONE_BY_NAME_SUCCESS,
  payload: {
    name,
    found,
  },
  meta,
});

/**
 * @param {string} groupId
 * @param {Object} [meta={}]
 * @returns {import('../types/actions').FetchCurrencyGroupAction}
 */
export const getCurrency = (groupId, meta = {}) => ({
  type: actionTypes.FETCH_GROUP_CURRENCY,
  payload: {
    request: {
      method: 'GET',
      url: `${GROUPS_API_URL}/${groupId}/currency`,
    },
  },
  meta,
});

// ===========================================================================
// Other actions
// ===========================================================================

/**
 * Selects a group by its ID with optional projection parameters.
 * @param {string} groupId - The unique identifier of the group to select.
 * @param {{ projection?: string }} [params={ projection: PROJECTION.CONFIG }] - Query parameters for the selection.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').SelectGroupAction}
 */
export const selectGroup = (groupId, params = { projection: PROJECTION.CONFIG }, meta = {}) => ({
  type: actionTypes.SELECT_GROUP,
  payload: { groupId, params },
  meta,
});

/**
 * @param {boolean} payload
 * @param {Object} [meta={}]
 * @return {import('modules/global/generic.types').Action<boolean>}
 */
export const setGroupConfigReady = (payload, meta = {}) => ({
  type: actionTypes.GROUP_CONFIG_IS_READY,
  payload,
  meta,
});

/**
 * Assigns a template to all dealers in a group.
 * @param {string} groupId - The group ID.
 * @param {string} templateId - The template ID to assign.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').AssignTemplateFromGroupAction}
 */
export const assignTemplateFromGroupRequest = (groupId, templateId, meta = {}) => ({
  type: actionTypes.ASSIGN_TEMPLATE_FROM_GROUP_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${GROUPS_API_URL}/${groupId}/dealers/templates/${templateId}/assign`,
    },
  },
  meta,
});

/**
 * Success action for assigning a template from group.
 * @param {unknown} payload - The response payload.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').AssignTemplateFromGroupSuccessAction}
 */
export const assignTemplateFromGroupSuccess = (payload, meta = {}) => ({
  type: actionTypes.ASSIGN_TEMPLATE_FROM_GROUP_SUCCESS,
  payload,
  meta,
});

/**
 * Failure action for assigning a template from group.
 * @param {string} error - The error message.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').AssignTemplateFromGroupFailureAction}
 */
export const assignTemplateFromGroupFailure = (error, meta = {}) => ({
  type: actionTypes.ASSIGN_TEMPLATE_FROM_GROUP_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * Returns the appropriate assign endpoint based on entity type.
 * @param {string} entity - The entity type.
 * @param {{ groupId: string, id: string }} options - The group and entity IDs.
 * @returns {string} The endpoint URL.
 */
const getAssignEndpoint = (entity, { groupId, id }) => {
  switch (entity) {
    case 'questionFormModels':
      return `${QUESTION_MODELS_API_URL}/${id}/assign`;
    case 'vehicleCheckModel':
      return `${VEHICLE_CHECK_MODELS_API_URL}/${id}/assign`;
    default:
      return `${GROUPS_API_URL}/${groupId}/dealers/${entity}/${id}/assign`;
  }
};

/**
 * Assigns an entity to all dealers in a group.
 * @param {string} groupId - The group ID.
 * @param {string} entityId - The entity ID to assign.
 * @param {Object} data - The data to send with the request.
 * @param {string} entityName - The type of entity being assigned.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').AssignEntityFromGroupAction}
 */
export const assignEntityFromGroup = (groupId, entityId, data, entityName, meta = {}) => ({
  type: actionTypes.ASSIGN_ENTITY_FROM_GROUP_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: getAssignEndpoint(entityName, { groupId, id: entityId }),
      data,
    },
  },
  meta: {
    source: entityName,
    ...meta,
  },
});

/**
 * Opens the assign modal with the given payload.
 * @param {import('../types/actions').AssignModalPayload} payload - The modal payload.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').OpenAssignModalAction}
 */
export const openAssignModal = (payload, meta = {}) => ({
  type: actionTypes.OPEN_ASSIGN_MODAL,
  payload,
  meta,
});

/**
 * Hides the assign modal.
 * @param {Object} [meta={}] - Optional metadata for the action.
 * @returns {import('../types/actions').HideAssignModalAction}
 */
export const hideAssignModal = (meta = {}) => ({
  type: actionTypes.HIDE_ASSIGN_MODAL,
  meta,
});

/**
 * Initializes a group tab.
 * @param {boolean} initialized
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").InitializedGroupTabAction}
 */
export const initializedGroupTab = (initialized, meta = {}) => ({
  type: actionTypes.INITIALIZED_GROUP_TAB,
  payload: initialized,
  meta,
});

/**
 * Assigns a group field configuration to dealerships for a specific entity.
 * @param {string} groupId
 * @param {string} entity
 * @param {import("../types/actions").AssignmentGroupToDealersPayload} data
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").AssignmentGroupToDealersAction}
 */
export const assignGroupToDealer = (groupId, entity, data, meta = {}) => ({
  type: actionTypes.ASSIGNMENT_GROUP_TO_DEALERS_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${GROUPS_API_URL}/${groupId}/assignments/${entity}`,
      data,
    },
    entity,
  },
  meta,
});

/**
 * Success action for assigning a group to dealers.
 * @param {string} entity
 * @param {import("../types/actions").AssignmentGroupToDealersPayload} data
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").AssignmentGroupToDealersSuccessAction}
 */
export const assignGroupToDealerSuccess = (entity, data, meta = {}) => ({
  type: actionTypes.ASSIGNMENT_GROUP_TO_DEALERS_SUCCESS,
  payload: {
    entity,
    dataRequest: data
  },
  meta,
});

export const setGroupManagementReady = (payload, meta = {}) => ({
  type: actionTypes.GROUP_MANAGEMENT_IS_READY,
  payload,
  meta
});
